import { effect, inject, Type, untracked } from "@angular/core";
import { patchState, signalStore, withHooks, withMethods, withProps, withState } from "@ngrx/signals";
import { type SelectEntityId, upsertEntities, withEntities } from "@ngrx/signals/entities";
import { Item } from "../../pojo/Item";
import type { S25ItemI } from "../../pojo/S25ItemI";
import { S25Util } from "../s25-util";
import { LoginStore } from "../../stores/login.store";
import {
    FetchConfig,
    IdItem,
    InternalState,
    ItemCriteria,
    ItemStoreId,
    ItemStoreID,
    QueryKey,
    QueryRequest,
    StoredItem,
} from "./item.store.internal";

type FetchOptions = {
    fetch?: FetchConfig;
};
export type ItemOptions = FetchOptions & {
    /**
     * Hydrate existing items, rather than returning new ones
     */
    hydrate?: boolean;
};

export interface ItemStore<T extends number | string | undefined> {
    /**
     * Same as getAll, for a single item. This function is reactive, so you can
     * call it within a computed() signal for the latest value after it loads.
     */
    get(item: IdItem<T>, options?: ItemOptions): StoredItem<T>;
    get(id: ItemStoreID, options?: FetchOptions): StoredItem<T>;

    /**
     * Get the current known representation of the given items or IDs, along with
     * a _loaded property indicating which properties have been fetched already.
     * If the items have not been fetched yet, they will be requested in the next
     * batch. This function is reactive, so you can call it within a computed()
     * signal for the latest values after any items are loaded.
     */
    getAll(items: IdItem<T>[], options?: ItemOptions): StoredItem<T>[];
    getAll(criteria: ItemStoreID[] | ItemCriteria.Query, options?: FetchOptions): StoredItem<T>[];

    /**
     * Wait for a loaded copy of the item. If the data is not loaded, it will
     * be added to the next available batch. So fear not making several fetch
     * requests in a row; they will be combined and deduplicated!
     */
    fetch(item: IdItem<T>, options?: ItemOptions): Promise<Item<T>>;
    fetch(id: ItemStoreID, options?: FetchOptions): Promise<Item<T>>;

    /**
     * If you pass a set of params here, it will send a request if that
     * specific set of params has not been invoked before. HOWEVER, individual
     * loaders will decide which params are relevant for fetching. E.g., if you
     * request { customFilterValue: "123" } from eventTypes, that loader is
     * configured to ignore all params, so you will not trigger an additional
     * HTTP request if you later request { customFilterValue: "456" }.
     *
     * Another example is eventCategories, which uses `useSecurity`, but not
     * `customFilterValue`. So if you request { useSecurity: true, customFilterValue: 123},
     * then later request { useSecurity: true, customFilterValue: 456 }, you will
     * not trigger a second HTTP request.
     */
    fetchAll(items: IdItem<T>[], options?: ItemOptions): Promise<Item<T>[]>;
    fetchAll(criteria: ItemStoreID[] | ItemCriteria.Query, options?: FetchOptions): Promise<Item<T>[]>;

    /**
     * Storing items will make them available for future requests. I'm not sure
     * yet whether it makes sense to add these to existing services, or add them
     * to invocation sites... that's TBD at this point.
     */
    storeItem(item: IdItem<T>, fetched: FetchConfig): Item<T>;
    storeAllItems(items: IdItem<T>[], fetched: FetchConfig): Item<T>[];

    /**
     * Drop cached fetchAll results for one query so the next fetchAll refetches.
     * Pass the same params object shape used in fetchAll({ params: ... }).
     * Omit params to clear the "everything" query only.
     */
    invalidateQueries(params?: Record<string, unknown>): void;
}

export namespace ItemStore {
    export const create = createItemStore;

    export type Config<T extends number | string | undefined> = {
        typeId: T;
        loaders: Loader<T>[];
        storeHandlers?: StoreHandler[];
        silentFailure?: boolean;
        /**
         * How many times an individual id may be (re)fetched before it's
         * considered exhausted.
         */
        maxRetriesPerItem?: number;
    };

    export type Loader<T extends string | number | undefined> = Loader.FetchSome<T> | Loader.FetchAll<T>;
    export namespace Loader {
        export type LoaderParams = Record<string, any> & { loggedIn: boolean };

        export type Base = {
            /**
             * If "handles" is omitted, it's assumed that it will fetch all data
             */
            handles?: FetchConfig;
            /**
             * Max retries for each query. Id-based loading will use store config
             */
            maxRetries?: number;
        };
        export type FetchSome<T extends string | number | undefined> = Base & {
            fetch: (ids: number[], params: LoaderParams) => Promise<IdItem<T>[]>;
        };
        export type FetchAll<T extends string | number | undefined> = Base & {
            fetchAll: (params?: Record<string, any>) => Promise<IdItem<T>[]>;
            // TODO: add post-processing for query results,
            //  while storing full result set
            // filterQueryResult: (results: IdItem<T>[], params?: Record<string, any>) =>
            /**
             * Gets the pertinent params that will be passed to fetchAll.
             * - If omitted, or returns undefined or {}, the loader is
             *   treated as an "everything" loader.
             * - If it returns false, the loader refuses the request
             *   (e.g. a required param is missing).
             */
            getParams?: (params: LoaderParams) => Record<string, any> | undefined | false;
        };

        export function FetchSome<T extends string | number | undefined>(loader: Loader<T>): loader is FetchSome<T> {
            return "fetch" in loader;
        }

        export function FetchAll<T extends string | number | undefined>(loader: Loader<T>): loader is FetchAll<T> {
            return "fetchAll" in loader;
        }

        export function filterParams(
            rawParams: LoaderParams,
            relevantParams: string[],
        ): Record<string, unknown> | undefined {
            return relevantParams.reduce((params: Record<string, unknown> | undefined, thisParam) => {
                if (S25Util.isDefined(rawParams[thisParam])) {
                    params ??= {};
                    params[thisParam] = rawParams[thisParam];
                }
                return params;
            }, undefined);
        }
    }

    export type StoreHandler = {
        /**
         * If "handles" is omitted, it's assumed that it should apply to
         * any fetched item value
         */
        handles?: FetchConfig;
        handle: (item: S25ItemI<any>) => void;
    };

    export type AwaitingResponse<T> = {
        resolve: (item: Item<T>) => void;
        reject: (error: unknown) => void;
        fetch: FetchConfig;
    };
}

const selectId: SelectEntityId<StoredItem<any>> = (item) => S25Util.toInt(item.itemId);

function createItemStore<T extends number | string | undefined>(config: ItemStore.Config<T>) {
    const typeId = config.typeId;
    const maxRetriesPerItem = config.maxRetriesPerItem ?? 1;
    const fetchAllLoaders = config.loaders.filter(ItemStore.Loader.FetchAll<T>);
    const awaitingResponse: Record<number, ItemStore.AwaitingResponse<T>[]> = {};
    const awaitingQueryResponse: Partial<
        Record<QueryKey, Array<{ fetch: FetchConfig; resolve: () => void; reject: (error: unknown) => void }>>
    > = {};
    const queryResultIds = new Map<QueryKey, number[]>();

    type ResolvedLoader = { loader: ItemStore.Loader.FetchAll<T>; normalizedParams?: Record<string, unknown> };
    function resolveLoader(rawParams?: Record<string, unknown>, loggedIn?: boolean): ResolvedLoader | undefined {
        const loaderParams: ItemStore.Loader.LoaderParams = { ...rawParams, loggedIn: loggedIn ?? false };
        for (const loader of fetchAllLoaders) {
            if (!loader.getParams) return { loader };
            const result = loader.getParams(loaderParams);
            if (result !== false) return { loader, normalizedParams: result ?? undefined };
        }
        return undefined;
    }

    function logError(message: string, cause?: unknown): Error {
        const error = new Error(`ItemStore(${config.typeId}): ${message}`, cause != null ? { cause } : undefined);
        if (!config.silentFailure) console.error({ error, cause });
        return error;
    }

    function logAndThrow(message: string, cause?: unknown): never {
        throw logError(message, cause);
    }

    return signalStore(
        { providedIn: "root" },
        withEntities<StoredItem<T>>(),
        withProps(() => ({
            _loginStore: inject(LoginStore),
        })),
        withState<InternalState>({
            _wanted: {} as Record<number, FetchConfig>,
            _inFlight: {} as Record<number, FetchConfig>,
            _failures: {} as Record<number, number>,
            _wantedQueries: {} as Record<QueryKey, QueryRequest>,
            _inFlightQueries: {} as Record<QueryKey, QueryRequest>,
            _loadedQueries: {} as Record<QueryKey, FetchConfig>,
            _failedQueries: {} as Record<QueryKey, number>,
        }),
        /**
         * Implement all ItemStore methods
         */
        withMethods((store): ItemStore<T> => {
            return {
                get,
                getAll,
                fetch,
                fetchAll,
                storeItem,
                storeAllItems,
                invalidateQueries,
            };

            function invalidateQueries(rawParams?: Record<string, unknown>): void {
                const resolved = resolveLoader(rawParams);
                const queryKey: QueryKey = resolved ? QueryKey(resolved.normalizedParams) : "everything";
                queryResultIds.delete(queryKey);
                patchState(store, InternalState.clearQueryCache(queryKey));
            }

            function get(idOrItem: ItemStoreID | IdItem<T>, options?: ItemOptions): StoredItem<T> {
                if (typeof idOrItem === "object") return getAll([idOrItem], options)[0];
                return getAll([idOrItem], options)[0];
            }

            function getAll(criteria: ItemCriteria<T>, options?: ItemOptions): StoredItem<T>[] {
                if (ItemCriteria.isQuery(criteria)) {
                    const { queryKey } = handleQueryRequest(criteria, options);
                    return getQueryItems(queryKey);
                } else {
                    const { results } = processRequestedItems(criteria, options, false);
                    if (options?.hydrate && criteria.length > 0 && typeof criteria[0] === "object") {
                        (criteria as S25ItemI<T>[]).forEach((item, i) => Object.assign(item, results[i]));
                        return criteria as StoredItem<T>[];
                    }
                    return results;
                }
            }

            async function fetch(idOrItem: ItemStoreID | IdItem<T>, options?: ItemOptions): Promise<Item<T>> {
                if (typeof idOrItem === "object") return (await fetchAll([idOrItem], options))[0];
                return (await fetchAll([idOrItem], options))[0];
            }

            async function fetchAll(criteria: ItemCriteria<T>, options?: ItemOptions): Promise<Item<T>[]> {
                if (ItemCriteria.isQuery(criteria)) {
                    const { queryKey, fetchConfig, isLoaded } = handleQueryRequest(criteria, options);
                    if (!isLoaded) {
                        await new Promise<void>((resolve, reject) => {
                            (awaitingQueryResponse[queryKey] ??= []).push({ fetch: fetchConfig, resolve, reject });
                        });
                    }
                    return getQueryItems(queryKey);
                } else {
                    const { results, fetch, notFetched } = processRequestedItems(criteria, options, true);
                    if (notFetched.length) {
                        await new Promise<void>((resolve, reject) => {
                            let remaining = notFetched.length;
                            for (const id of notFetched) {
                                (awaitingResponse[id] ??= []).push({
                                    fetch,
                                    resolve: () => {
                                        if (--remaining === 0) resolve();
                                    },
                                    reject,
                                });
                            }
                        });
                    }
                    const freshEntityMap = store.entityMap();
                    if (options?.hydrate && criteria.length > 0 && typeof criteria[0] === "object") {
                        criteria.forEach((item) => Object.assign(item, freshEntityMap[ItemStoreId.fromItem(item)]));
                        return criteria as Item<T>[];
                    } else {
                        return results.map((item) =>
                            StoredItem.toItem<T>(freshEntityMap[item.itemId], item.itemId, typeId),
                        );
                    }
                }
            }

            function storeItem(item: IdItem<T>, fetched: FetchConfig): Item<T> {
                return storeAllItems([item], fetched)[0];
            }

            function storeAllItems(items: IdItem<T>[], fetched: FetchConfig): Item<T>[] {
                FetchConfig.assertFetchConfigMatches(items, fetched);
                const stored: StoredItem<T>[] = items.map((item) => {
                    const intId = S25Util.toInt(item.itemId);
                    const existing = store.entityMap()[intId];
                    const merged: StoredItem<T> = {
                        ...existing,
                        ...item,
                        itemId: intId,
                        ...(typeId ? { itemTypeId: typeId } : {}),
                        _loaded: FetchConfig.merge(existing?._loaded, fetched),
                    };
                    return merged;
                });
                config.storeHandlers
                    ?.filter((h) => FetchConfig.isSuperset(fetched, h.handles ?? {}))
                    .forEach((handler) => stored.forEach((item) => handler.handle({ ...item })));
                patchState(store, upsertEntities<StoredItem<T>>(stored, { selectId }));
                for (const item of stored) {
                    const awaiting = awaitingResponse[item.itemId];
                    if (!awaiting) continue;
                    const unresolved: ItemStore.AwaitingResponse<T>[] = [];
                    for (const a of awaiting) {
                        if (StoredItem.hasProps(item, a.fetch)) a.resolve(item);
                        else unresolved.push(a);
                    }
                    if (!unresolved.length) delete awaitingResponse[item.itemId];
                    else awaitingResponse[item.itemId] = unresolved;
                }
                return stored.map((item) => ({ ...item }));
            }

            function processRequestedItems(
                criteria: ItemStoreID[] | S25ItemI<T>[],
                options?: FetchOptions,
                isFetch = false,
            ) {
                const fetch = options?.fetch ?? FetchConfig.Default;
                const entityMap = store.entityMap();

                const notFetched: number[] = [];
                const needsFetch: number[] = [];
                const ids = criteria.map(ItemStoreId.fromItem);
                const pendingEverythingFetch = FetchConfig.isSuperset(
                    store._inFlightQueries()["everything"]?.fetch,
                    fetch,
                );
                const results = ids.map((id) => {
                    const entity = entityMap[id];
                    if (!StoredItem.hasProps(entity, fetch)) {
                        notFetched.push(id);
                        if (!pendingEverythingFetch && !FetchConfig.isSuperset(store._inFlight()[id], fetch)) {
                            needsFetch.push(id);
                        }
                    }
                    return StoredItem.toItem<T>(entity, id, typeId);
                });
                if (notFetched.length) {
                    // If we encounter an ID that has failed too many times, fetches
                    // still need to know about the ID so they can reject the promise
                    const idsToWant = isFetch ? ids : withinRetryLimit(ids);
                    if (idsToWant.length)
                        Promise.resolve().then(() => patchState(store, InternalState.addIdsToWanted(idsToWant, fetch)));
                }
                return { ids, fetch, results, notFetched, needsFetch };
            }

            function withinRetryLimit(ids: number[]): number[] {
                const failures = store._failures();
                return ids.filter((id) => (failures[id] ?? 0) < maxRetriesPerItem);
            }

            function handleQueryRequest<T extends string | number | undefined>(
                criteria: ItemCriteria<T>,
                options?: FetchOptions,
            ) {
                const rawParams = "params" in criteria ? criteria.params : undefined;
                const resolved = resolveLoader(rawParams);
                if (!resolved) {
                    logAndThrow("No loader configured for query", criteria);
                }
                const queryKey = QueryKey(resolved.normalizedParams);
                const fetchConfig = options?.fetch ?? { all: true };
                const isLoaded = FetchConfig.isSuperset(store._loadedQueries()[queryKey], fetchConfig);
                if (!isLoaded) {
                    const rawKey = QueryKey(rawParams);
                    Promise.resolve().then(() =>
                        patchState(store, InternalState.addQueryToWanted(rawKey, { rawParams, fetch: fetchConfig })),
                    );
                }
                return { queryKey, fetchConfig, isLoaded };
            }

            function getQueryItems(queryKey: QueryKey): StoredItem<T>[] {
                const entityMap = store.entityMap();
                const resultIds = queryResultIds.get(queryKey);
                if (resultIds) {
                    return resultIds.map((id) => StoredItem.toItem<T>(entityMap[id], id, typeId));
                } else if (queryKey === "everything") {
                    return Object.values(entityMap).map((entity) => ({ ...entity }));
                } else {
                    return [];
                }
            }
        }),
        /**
         * Attach _executeBatch to the store
         */
        withMethods((store) => {
            return {
                _executeBatch,
            };

            function _executeBatch() {
                const wanted = store._wanted();
                const wantedQueries = store._wantedQueries();
                if (!Object.keys(wanted).length && !Object.keys(wantedQueries).length) return;

                untracked(() => {
                    const queriesToFetch = resolveQueries(wantedQueries);
                    patchState(store, InternalState.clearWanted());
                    buildQueryRequests(queriesToFetch);
                    if (!("everything" in queriesToFetch)) {
                        buildFetchRequests(wanted);
                    }
                });
            }

            type ResolvedQuery = {
                loader: ItemStore.Loader.FetchAll<T>;
                params?: Record<string, unknown>;
                fetch: FetchConfig;
            };

            /**
             * Resolve raw wanted queries: pick a loader, normalize params,
             * deduplicate by normalized QueryKey, and filter out already-loaded
             * or in-flight queries.
             */
            function resolveQueries(
                wanted: Partial<Record<QueryKey, QueryRequest>>,
            ): Partial<Record<QueryKey, ResolvedQuery>> {
                const loaded = store._loadedQueries();
                const pending = store._inFlightQueries();
                const result: Partial<Record<QueryKey, ResolvedQuery>> = {};
                for (const req of Object.values(wanted).filter(S25Util.isDefined)) {
                    const resolved = resolveLoader(req.rawParams);
                    if (!resolved) continue;
                    const queryKey = QueryKey(resolved.normalizedParams);
                    if (FetchConfig.isSuperset(loaded[queryKey], req.fetch)) continue;
                    if (FetchConfig.isSuperset(pending[queryKey]?.fetch, req.fetch)) continue;
                    const existing = result[queryKey];
                    if (existing) {
                        existing.fetch = FetchConfig.merge(existing.fetch, req.fetch);
                    } else {
                        result[queryKey] = {
                            loader: resolved.loader,
                            params: resolved.normalizedParams,
                            fetch: req.fetch,
                        };
                    }
                }
                return result;
            }

            function buildFetchRequests(toFetch: Record<number, FetchConfig>) {
                const loaderParams: ItemStore.Loader.LoaderParams = { loggedIn: store._loginStore.loggedIn() };
                const failures = store._failures();
                batchItemRequests(toFetch).forEach((ids, loader) => {
                    const fetch = loader.handles ?? { all: true };
                    const exceeded = ids.filter((id) => (failures[id] ?? 0) >= maxRetriesPerItem);
                    const fetchableIds = ids.filter((id) => (failures[id] ?? 0) < maxRetriesPerItem);
                    if (exceeded.length) {
                        registerIdRequestComplete(
                            exceeded,
                            fetch,
                            logError(`Item(s) ${exceeded.join(", ")} exceeded ${maxRetriesPerItem} retries`),
                        );
                    }
                    if (!fetchableIds.length) return;

                    let promise: Promise<IdItem<T>[]>;
                    if (ItemStore.Loader.FetchSome<T>(loader)) {
                        promise = loader.fetch(fetchableIds, loaderParams);
                    } else {
                        const params = loader.getParams?.(loaderParams);
                        if (params === false) {
                            logAndThrow("Loader refused request", loaderParams);
                        }
                        promise = loader.fetchAll(params ?? {});
                    }
                    patchState(store, InternalState.markIdsInFlight(fetchableIds, fetch));
                    promise
                        .then((fetched) => {
                            registerIdRequestComplete(fetchableIds, fetch);
                            store.storeAllItems(fetched, fetch);
                        })
                        .catch((error) =>
                            registerIdRequestComplete(fetchableIds, fetch, logError("Fetch failed", error)),
                        );
                });
            }

            function batchItemRequests(items: Record<number, FetchConfig>): Map<ItemStore.Loader<T>, number[]> {
                const idLoaders = config.loaders.filter(ItemStore.Loader.FetchSome<T>);
                const result = Object.entries(items).reduce((batches, [id, fetch]) => {
                    for (let i = 0; i < idLoaders.length && !FetchConfig.isEmpty(fetch); i++) {
                        const loader = idLoaders[i];
                        const handled = FetchConfig.intersection(loader.handles ?? { all: true }, fetch);
                        if (!FetchConfig.isEmpty(handled)) {
                            fetch = FetchConfig.subtract(fetch, handled);
                            if (!batches.has(loader)) batches.set(loader, []);
                            batches.get(loader)!.push(Number(id));
                        }
                    }
                    return batches;
                }, new Map<ItemStore.Loader<T>, number[]>());

                if (!result.size) {
                    const fallback = config.loaders.find(ItemStore.Loader.FetchAll<T>);
                    if (fallback) result.set(fallback, Object.keys(items).map(Number));
                }

                return result;
            }

            function buildQueryRequests(queries: Partial<Record<QueryKey, ResolvedQuery>>) {
                for (const [queryKey, query] of Object.entries(queries) as [QueryKey, ResolvedQuery][]) {
                    const { loader } = query;
                    if ((store._failedQueries()[queryKey] ?? 0) >= (loader.maxRetries ?? 1)) {
                        registerQueryComplete(
                            queryKey,
                            logError(`Query "${queryKey}" exceeded ${loader.maxRetries ?? 1} retries`),
                        );
                        continue;
                    }
                    const handles = loader.handles ?? { all: true };
                    patchState(
                        store,
                        InternalState.markQueryInFlight(queryKey, { rawParams: query.params, fetch: query.fetch }),
                    );
                    loader
                        .fetchAll(query.params ?? {})
                        .then((fetched) => registerQuerySuccess(queryKey, fetched, handles))
                        .catch((error) => registerQueryComplete(queryKey, logError("Query failed", error)));
                }
            }

            function registerIdRequestComplete(ids: number[], fetch: FetchConfig, error?: unknown) {
                patchState(store, InternalState.markIdsComplete(ids, fetch), InternalState.trackFailedIds(ids, error));
                if (error) {
                    for (const id of ids) {
                        const awaiting = awaitingResponse[id];
                        if (!awaiting) continue;
                        for (const a of awaiting) a.reject(error);
                        delete awaitingResponse[id];
                    }
                }
            }

            function registerQuerySuccess(queryKey: QueryKey, fetched: IdItem<T>[], handles: FetchConfig) {
                store.storeAllItems(fetched, handles);
                patchState(store, InternalState.markQueryLoaded(queryKey, handles));
                queryResultIds.set(
                    queryKey,
                    fetched.map((i) => S25Util.toInt(i.itemId)),
                );
                registerQueryComplete(queryKey);
            }

            function registerQueryComplete(queryKey: QueryKey, error?: unknown) {
                patchState(
                    store,
                    InternalState.markQueryComplete(queryKey),
                    InternalState.trackFailedQuery(queryKey, error),
                );

                awaitingQueryResponse[queryKey]?.forEach((callback) =>
                    error ? callback.reject(error) : callback.resolve(),
                );
                delete awaitingQueryResponse[queryKey];

                if (error) {
                    const inFlight = store._inFlight();
                    for (const [idStr, awaiting] of Object.entries(awaitingResponse)) {
                        const id = Number(idStr);
                        if (inFlight[id]) continue;
                        for (const a of awaiting) a.reject(error);
                        delete awaitingResponse[id];
                    }
                }
            }
        }),
        withHooks({
            onInit(store) {
                effect(() => store._executeBatch());
            },
        }),
    ) satisfies Type<ItemStore<T>>;
}
